/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType, PortalType } from '../../config/enum';
import { detectPortalType } from '../../contexts/PortalContext/PortalProvider';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  SessionAPIResponse,
  SessionInitialProperties,
  CommercialSessionInterface,
} from '../../interface/charging-session.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import paymentApi from '../../utils/api/payments';

import { setNotificationMessage } from './NotificationManagementSlice';

// Define the type of data expected
interface FetchParameters {
  search?: string;
  page?: number;
  limit?: number;
}

const portalType = detectPortalType();

/**
 * a thunk to fetch sessions (payments v2)
 */
export const fetchAllSessions = createAsyncThunk<
  SessionAPIResponse<CommercialSessionInterface>,
  FetchParameters
>('charging-session/fetchAllSessions', async (data: FetchParameters, { dispatch }) => {
  try {
    const response = await paymentApi.fetchChargingSessions(removeEmptyKeys(data));

    return {
      ...response.data,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        id: roleData._id,
      })),
    };
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message,
        code: error?.response?.data?.code,
        status: true,
      }),
    );

    throw error;
  }
});

// a thunk to refresh sessions
export const refreshChargingSessions = createAsyncThunk<
  SessionAPIResponse<CommercialSessionInterface>,
  FetchParameters
>('charging-session/refreshChargingSessions', async (data: FetchParameters) => {
  const response = await paymentApi.fetchChargingSessions(removeEmptyKeys(data));
  return {
    ...response.data,
    results: response.data.results.map((roleData: any) => ({
      ...roleData,
      date: ConvertUTCtoDate(roleData.createdAt),
      id: roleData._id,
    })),
  };
});


// a thunk to fetch session filters
export const fetchListSessionFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'charging-session/fetchListSessionFilters',
  async (_, { dispatch }) => {
    try {
      const response = await paymentApi.fetchChargingSessionsListFilters();

      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);

      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });

      throw error;
    }
  },
);

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param {any} date The date object to format.
 * @returns {any} The formatted date string in YYYY-MM-DD format.
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

/**
 * Filter date format func
 * @param date
 * @returns
 */
const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  ...(portalType === PortalType.NETWORKS
    ? [
      {
        name: 'createdDateRange',
        key: 'startDate_endDate',
        value: '',
        key1: 'startDate',
        value1: formatDate(yesterday),
        key2: 'endDate',
        value2: formatDate(today),
        type: filterType.DATE_RANGE,
        listOfValue: [],
        applyFilter: true,
        enable: true,
        disableClearFilter: true,
        disableClearFilterMessage: 'Date filter is required!',
        FEAddedFilter: true,
      } as CommonFilterProperties,
    ]
    : []),
];
// Initial state
const initialState: SessionInitialProperties<CommercialSessionInterface> = {
  payments: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
  TotalFinising: 0,
  totalInprocess: 0,
  totalterminated: 0,
};

// Create the slice
export const chargingSessionSlice = createSlice({
  name: 'charging-session',
  initialState,
  reducers: {
    /**
     * Update session filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateSessionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableSessionFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSessionSortData: (
      state,
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>,
    ) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAllSessions.pending,
        (state: SessionInitialProperties<CommercialSessionInterface>) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchAllSessions.fulfilled,
        (
          state: SessionInitialProperties<CommercialSessionInterface>,
          action: { payload: SessionAPIResponse<CommercialSessionInterface> },
        ) => {
          state.loading = false;
          state.payments = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.TotalFinising = action.payload.TotalFinising;
          state.totalInprocess = action.payload.totalInprocess;
          state.totalterminated = action.payload.totalterminated;
        },
      )
      .addCase(
        fetchAllSessions.rejected,
        (state: SessionInitialProperties<CommercialSessionInterface>) => {
          state.loading = false;
          // Handle error if needed
        },
      );
    builder.addCase(
      refreshChargingSessions.fulfilled,
      (
        state: SessionInitialProperties<CommercialSessionInterface>,
        action: { payload: SessionAPIResponse<CommercialSessionInterface> },
      ) => {
        state.payments = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.TotalFinising = action.payload.TotalFinising;
        state.totalInprocess = action.payload.totalInprocess;
        state.totalterminated = action.payload.totalterminated;
      },
    );
    builder
      .addCase(fetchListSessionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListSessionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchListSessionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateSessionFilters, disableSessionFiltersApplied, setSessionSortData } =
  chargingSessionSlice.actions;
export default chargingSessionSlice.reducer;
